/**
 * itineraryRelayApi — relay notice creation on itinerary status transitions
 *
 * Called from admin itineraries UI when status is set to "confirmed" or "updated".
 * Finds or creates conversation_threads for customer + photographer, then inserts
 * system_notice messages and relay_events. Duplicate-safe: skips if the same
 * event was created within the last 30 seconds.
 */
import { getSupabaseClient } from "../lib/supabaseClient";

export async function createItineraryStatusNotice(
  orderId: string,
  newStatus: "confirmed" | "updated",
  city: string,
  shootDate: string
): Promise<{ success: boolean; threadsCreated: number }> {
  const supabase = getSupabaseClient();
  const now = new Date().toISOString();
  const statusLabel = newStatus === "confirmed" ? "已確認" : "已更新";

  // Duplicate guard — skip if same event fired within 30s for this order
  const { data: recent } = await supabase
    .from("relay_events")
    .select("id")
    .eq("event_type", `itinerary_${newStatus}`)
    .gte("created_at", new Date(Date.now() - 30_000).toISOString())
    .limit(1);

  if (recent && recent.length > 0) {
    return { success: true, threadsCreated: 0 };
  }

  const participantTypes = ["customer", "photographer"] as const;
  let threadsCreated = 0;

  for (const participantType of participantTypes) {
    // Find existing thread for this order + participant type
    const { data: existing } = await supabase
      .from("conversation_threads")
      .select("id")
      .eq("order_id", orderId)
      .eq("participant_type", participantType)
      .limit(1)
      .maybeSingle();

    let threadId = existing?.id as string | undefined;

    if (!threadId) {
      const subject =
        participantType === "customer"
          ? `攝影服務集合資訊${statusLabel}`
          : `攝影任務集合資訊${statusLabel}`;

      const { data: newThread } = await supabase
        .from("conversation_threads")
        .insert({
          order_id: orderId,
          participant_type: participantType,
          subject,
          status: "open",
          thread_status: "open",
          risk_level: "none",
          risk_summary: "",
          is_main: true,
        })
        .select("id")
        .maybeSingle();

      threadId = (newThread as { id: string } | null)?.id;
      threadsCreated++;
    }

    if (!threadId) continue;

    const body =
      participantType === "customer"
        ? `您的攝影服務集合資訊已${statusLabel}（${city} · ${shootDate}）。請至「我的預訂」查看最新集合地點與時間。如有問題請聯絡平台。`
        : `攝影任務集合資訊已${statusLabel}：${city} · ${shootDate}。請至指派頁面確認最新集合地點與時間詳情。`;

    await supabase.from("conversation_messages").insert({
      thread_id: threadId,
      sender_type: "system",
      sender_id: "system",
      body,
      message_type: "system_notice",
      risk_flags: [],
      is_blocked: false,
      is_read_admin: true,
      is_read_photographer: participantType !== "photographer",
      is_read_customer: participantType !== "customer",
    });

    await supabase
      .from("conversation_threads")
      .update({ last_message_at: now, updated_at: now })
      .eq("id", threadId);

    await supabase.from("relay_events").insert({
      thread_id: threadId,
      event_type: `itinerary_${newStatus}`,
      actor_type: "system",
      actor_id: "system",
      payload: { order_id: orderId, status: newStatus, city, shoot_date: shootDate },
    });
  }

  return { success: true, threadsCreated };
}

/** Fetch latest system_notice for a set of order IDs and participant type */
export async function fetchLatestItineraryNotices(
  orderIds: string[],
  participantType: "customer" | "photographer"
): Promise<Map<string, string>> {
  if (orderIds.length === 0) return new Map();
  const supabase = getSupabaseClient();

  const { data: threads } = await supabase
    .from("conversation_threads")
    .select("id, order_id")
    .in("order_id", orderIds)
    .eq("participant_type", participantType);

  if (!threads || threads.length === 0) return new Map();

  const threadIds = (threads as { id: string; order_id: string }[]).map((t) => t.id);
  const threadOrderMap = new Map(
    (threads as { id: string; order_id: string }[]).map((t) => [t.id, t.order_id])
  );

  const { data: messages } = await supabase
    .from("conversation_messages")
    .select("thread_id, body, created_at")
    .in("thread_id", threadIds)
    .eq("message_type", "system_notice")
    .order("created_at", { ascending: false });

  const result = new Map<string, string>();
  for (const msg of (messages as { thread_id: string; body: string }[]) ?? []) {
    const orderId = threadOrderMap.get(msg.thread_id);
    if (orderId && !result.has(orderId)) {
      result.set(orderId, msg.body);
    }
  }
  return result;
}
